<template>
  <v-select
    class="paginated-select"
    :options="options"
    :modelValue="selectedOption"
    :append-to-body="appendToBody"
    :clearable="clearable"
    :create-option="createCustomOption"
    :disabled="disabled"
    :filterable="false"
    :get-option-label="getOptionLabel"
    :loading="isLoading"
    :taggable="allowCustom"
    :required="required"
    @search="onSearch"
    @open="onOpen"
    @close="onClose"
    @update:modelValue="selectOption"
    @clear="selectOption(null)"
  >
    <template #option="option">
      <slot name="option" v-bind="option">
        <div>{{ getOptionLabel(option) }}</div>
      </slot>
    </template>
    <template #selected-option="option">
      <slot name="selected-option" v-bind="option">
        <div>{{ getOptionLabel(option) }}</div>
      </slot>
    </template>
    <template #list-footer>
      <li
        v-if="isLoading || hasMorePages"
        class="px-3 py-2 border-top text-center text-muted"
      >
        <span v-if="isLoading">Завантаження...</span>
      </li>
    </template>
    <template #no-options>
      <span v-if="isLoading">Завантаження...</span>
      <span v-else>{{ $filters.localize("select.noOptions") }}</span>
    </template>
  </v-select>
</template>

<script>
import vSelect from "vue-select";

export default {
  name: "PaginatedSelect",
  components: { vSelect },
  emits: ["update:modelValue", "update:option"],
  props: {
    modelValue: [String, Number],
    table: { type: String, required: true },
    searchMethod: { type: String, default: "search" },
    labelField: { type: String, default: "name" },
    valueField: { type: String, default: "id" },
    pageSize: { type: Number, default: 10 },
    disabled: Boolean,
    required: Boolean,
    clearable: { type: Boolean, default: true },
    appendToBody: { type: Boolean, default: true },
    debounce: { type: Number, default: 300 },
    allowCustom: Boolean,
  },
  data() {
    return {
      options: [],
      selectedOption: null,
      pagination: {
        page: 1,
        count: 0,
        packageSize: this.pageSize,
        totalPages: 0,
      },
      searchValue: "",
      loading: false,
      selectedOptionLoading: false,
      searchTimeout: null,
      requestId: 0,
      selectedOptionRequestId: 0,
      optionsAbortController: null,
      selectedOptionAbortController: null,
      scrollElement: null,
    };
  },
  computed: {
    isLoading() {
      return this.loading || this.selectedOptionLoading;
    },
    hasMorePages() {
      return this.pagination.page < this.pagination.totalPages;
    },
  },
  watch: {
    modelValue: {
      handler(value) {
        this.syncSelectedOption(value);
      },
      immediate: true,
    },
    table() {
      this.resetLookup();
    },
  },
  beforeUnmount() {
    window.clearTimeout(this.searchTimeout);
    this.cancelOptionsRequest();
    this.cancelSelectedOptionRequest();
    this.unbindScroll();
  },
  methods: {
    getOptionLabel(option) {
      if (typeof option === "string") {
        return option;
      }

      return option?.[this.labelField] ?? "";
    },
    selectOption(option) {
      if (this.allowCustom && typeof option === "string") {
        const value = option.trim();
        this.selectedOption = value
          ? { [this.labelField]: value, [this.valueField]: value }
          : null;
        this.searchValue = "";
        this.$emit("update:modelValue", value);
        this.$emit("update:option", null);
        return;
      }

      this.selectedOption = option ?? null;
      this.searchValue = "";
      this.$emit("update:modelValue", this.getOptionValue(option));
      this.$emit("update:option", option ?? null);
    },
    createCustomOption(value) {
      const label = value?.trim() ?? "";
      return { [this.labelField]: label, [this.valueField]: label };
    },
    getOptionValue(option) {
      if (!option) {
        return "";
      }

      if (typeof option === "string") {
        return option;
      }

      const value = option?.[this.valueField];
      if (value || !this.allowCustom) {
        return value ?? "";
      }

      return this.getOptionLabel(option);
    },
    async syncSelectedOption(value) {
      if (!value) {
        this.selectedOption = null;
        this.cancelSelectedOptionRequest();
        return;
      }

      const localOption = this.options.find((option) => option?.[this.valueField] === value);
      if (localOption) {
        this.selectedOption = localOption;
        return;
      }

      if (this.allowCustom && this.valueField !== "id") {
        this.selectedOption = { [this.labelField]: value, [this.valueField]: value };
        return;
      }

      const requestId = ++this.selectedOptionRequestId;
      this.cancelSelectedOptionRequest();
      const controller = new AbortController();
      this.selectedOptionAbortController = controller;
      this.selectedOptionLoading = true;

      try {
        const response = await this.$store.dispatch("index", {
          table: this.table,
          body: { id: value },
          signal: controller.signal,
        });

        if (requestId !== this.selectedOptionRequestId) {
          return;
        }

        const option = response.data?.data;
        if (option?.id === value) {
          this.selectedOption = option;
          this.options = this.mergeOptions(this.options, [option]);
        }
      } catch (error) {
        if (error.code !== "ERR_CANCELED") {
          throw error;
        }
      } finally {
        if (requestId === this.selectedOptionRequestId) {
          this.selectedOptionLoading = false;
          this.selectedOptionAbortController = null;
        }
      }
    },
    resetLookup() {
      this.pagination.page = 1;
      this.searchValue = "";
      this.options = this.selectedOption ? [this.selectedOption] : [];
    },
    onOpen() {
      this.bindScroll();
      if (this.options.length === 0 || (this.selectedOption && this.options.length === 1)) {
        this.pagination.page = 1;
        this.loadOptions({ append: false });
      }
    },
    onClose() {
      this.commitCustomSearchValue();
      this.unbindScroll();
    },
    onSearch(value) {
      this.searchValue = value?.trim() ?? "";
      this.pagination.page = 1;
      window.clearTimeout(this.searchTimeout);
      this.searchTimeout = window.setTimeout(() => {
        this.loadOptions({ append: false });
      }, this.debounce);
    },
    loadOptions({ append = false } = {}) {
      if (append && this.loading) {
        return Promise.resolve();
      }

      this.cancelOptionsRequest();
      const controller = new AbortController();
      this.optionsAbortController = controller;
      this.loading = true;
      const requestId = ++this.requestId;
      const params = new URLSearchParams({
        page: this.pagination.page,
        size: this.pageSize,
      });

      if (this.searchValue) {
        params.set("search", this.searchValue);
      }

      return this.$store
        .dispatch("get", {
          table: this.table,
          method: `${this.searchMethod}?${params.toString()}`,
          signal: controller.signal,
        })
        .then((response) => {
          if (requestId !== this.requestId) {
            return;
          }

          const data = response.data.data;
          this.pagination = data;
          const nextOptions = data.entities ?? [];
          this.options = append
            ? this.mergeOptions(this.options, nextOptions)
            : this.mergeOptions(this.selectedOption ? [this.selectedOption] : [], nextOptions);
        })
        .catch((error) => {
          if (error.code !== "ERR_CANCELED") {
            throw error;
          }
        })
        .finally(() => {
          if (requestId === this.requestId) {
            this.loading = false;
            this.optionsAbortController = null;
          }
          this.bindScroll();
        });
    },
    cancelOptionsRequest() {
      if (this.optionsAbortController) {
        this.optionsAbortController.abort();
        this.optionsAbortController = null;
      }
    },
    cancelSelectedOptionRequest() {
      if (this.selectedOptionAbortController) {
        this.selectedOptionAbortController.abort();
        this.selectedOptionAbortController = null;
      }
    },
    commitCustomSearchValue() {
      if (!this.allowCustom) {
        return;
      }

      const value = this.searchValue.trim();
      if (!value) {
        return;
      }

      if (value === this.getOptionLabel(this.selectedOption)) {
        this.searchValue = "";
        return;
      }

      this.selectOption(value);
    },
    mergeOptions(currentItems, nextItems) {
      const map = new Map();
      [...currentItems, ...nextItems].forEach((item) => {
        const key = item?.id ?? item?.[this.valueField] ?? item?.[this.labelField];
        if (key) {
          map.set(key, item);
        }
      });
      return [...map.values()];
    },
    bindScroll() {
      this.unbindScroll();
      this.$nextTick(() => {
        const dropdowns = document.querySelectorAll(".vs__dropdown-menu");
        const dropdownMenu = dropdowns[dropdowns.length - 1];
        if (!dropdownMenu) {
          return;
        }

        dropdownMenu.addEventListener("scroll", this.onScroll);
        this.scrollElement = dropdownMenu;
      });
    },
    unbindScroll() {
      if (!this.scrollElement) {
        return;
      }

      this.scrollElement.removeEventListener("scroll", this.onScroll);
      this.scrollElement = null;
    },
    onScroll(event) {
      const element = event.target;
      const threshold = 24;
      const isNearBottom =
        element.scrollTop + element.clientHeight >=
        element.scrollHeight - threshold;

      if (!isNearBottom || this.loading || !this.hasMorePages) {
        return;
      }

      this.pagination.page += 1;
      this.loadOptions({ append: true });
    },
  },
};
</script>

<style scoped>
.paginated-select :deep(.vs__spinner) {
  border-left-color: #4f7fa7;
}

.paginated-select.vs--loading :deep(.vs__open-indicator) {
  opacity: 0;
}
</style>
